/**
 * `<MapChangeDigest/>` — the gated orchestrator for the B3.6 digest (#765).
 *
 * It owns the digest's machinery so the panel stays pure UI:
 *   - resolves `since` from the member's stored "last looked" stamp
 *     (`lastLooked.ts`) — frozen ONCE on mount so the digest doesn't shift
 *     mid-session;
 *   - fetches the member's OWN slice (`useTaskChangeDigest`);
 *   - decides whether to surface the panel at all (only when there is
 *     content — a calm, no-noise default: an empty slice shows nothing);
 *   - on dismiss, hides the panel for this visit AND advances "last looked"
 *     to now (so the next visit's window starts here);
 *   - is itself behind the `task_change_digest` gate, so it is wholly absent
 *     when the key is off (default) and the live map is unchanged.
 *
 * BRAND-CRITICAL (§A9): everything rendered is the member's OWN slice the
 * endpoint returns. This container adds no aggregation, no cross-person read,
 * no count about anyone. It is a gentle orientation aid, on the member's
 * terms, dismissable.
 *
 * Mount intent: render this at the top of the map surface (lead or member).
 * It self-suppresses (renders null) when the gate is off, the slice is empty,
 * the member isn't resolved, or the member dismissed it this visit.
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import { useMemo, useState } from "react";

import { useTeamFeature } from "../../api/useTeamFeature";
import { resolveSince, writeLastLooked } from "../api/lastLooked";
import { useTaskChangeDigest } from "../api/useTaskChangeDigest";
import { ChangeDigestPanel } from "./ChangeDigestPanel";

export interface MapChangeDigestProps {
  teamId: string | undefined;
  /** The signed-in member's id on this team (scopes "last looked"); may be null. */
  membershipId: string | null;
  /** "open my work →" — jump to the member's own-work lens (optional). */
  onOpenMyWork?: (() => void) | undefined;
  /** Open a mentioned node (optional). */
  onOpenNode?: ((nodeId: string) => void) | undefined;
  /**
   * The team's IANA timezone — threaded to `ChangeDigestPanel` so the
   * "since your last visit" stamp renders in the team's local time (#906/#932).
   */
  teamTimezone?: string | undefined;
}

export function MapChangeDigest(props: MapChangeDigestProps) {
  const { teamId, membershipId, onOpenMyWork, onOpenNode, teamTimezone } = props;
  const enabled = useTeamFeature(teamId, "task_change_digest") === true;

  // Freeze `since` once per (team, member) so the digest is a stable "since
  // you last looked" snapshot, not a moving target as time passes in-session.
  // `resolveSince` reads the stored stamp at first render for this pair; we
  // intentionally do not re-resolve it as the clock advances.
  const since = useMemo(
    () => (teamId ? resolveSince(teamId, membershipId) : undefined),
    [teamId, membershipId],
  );

  const { digest, hasContent, isLoading, isError } = useTaskChangeDigest(
    enabled && teamId ? teamId : undefined,
    enabled ? since : undefined,
  );

  const [dismissed, setDismissed] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    // Advance "last looked" to now — next visit's window starts here. Done on
    // the member's terms (only when they dismiss), so they don't silently miss
    // changes that arrived while the panel sat unread.
    if (teamId) writeLastLooked(teamId, membershipId);
  };

  // Calm, no-noise defaults: nothing renders when the gate is off, while
  // loading, on a not-yet-deployed endpoint, on an empty slice, or once
  // dismissed. The member only ever sees the panel when there's something
  // genuinely on THEIR slice.
  if (!enabled || isLoading || isError || dismissed || !digest || !hasContent) {
    return null;
  }

  return (
    <ChangeDigestPanel
      digest={digest}
      onDismiss={dismiss}
      onOpenMyWork={onOpenMyWork}
      onOpenNode={onOpenNode}
      teamTimezone={teamTimezone}
    />
  );
}
